(() => {
  'use strict';


  /* =========================================================
     CONFIG
     ========================================================= */

  const CONFIG = Object.freeze({

    viewDelayMs: 500,

    viewRatio: 0.25,

    minVisibleHeightPx: 150,


    /*
     * Shopify Customer Events
     */
    shopifyEventView:
      'sculture:module_view',

    shopifyEventClick:
      'sculture:module_click',


    /*
     * Google Analytics 4
     */
    ga4EventView:
      'sct_module_view',

    ga4EventClick:
      'sct_module_click',


    /*
     * 現在SCULTUREで使用中のGA4
     */
    ga4MeasurementId:
      'G-YX131JS8XH',


    schemaVersion: 4,

  });



  /* =========================================================
     GLOBAL CONTEXT
     ========================================================= */

  const context =
    readContext();


  const query =
    new URLSearchParams(
      window.location.search
    );


  const debug =
    query.get(
      'sct_debug'
    ) === '1';


  const forcePublish =
    query.get(
      'sct_publish'
    ) === '1';


  const previewMode =
    isPreviewMode();



  /* =========================================================
     STATE
     ========================================================= */

  const observedModules =
    new WeakSet();


  const viewedModules =
    new WeakSet();


  const viewTimers =
    new WeakMap();


  const decoratedFilters =
    new WeakSet();



  /* =========================================================
     INTERSECTION OBSERVER
     ========================================================= */

  const observer =
    'IntersectionObserver' in window

      ? new IntersectionObserver(
          handleIntersections,
          {
            root: null,

            rootMargin: '0px',

            threshold: [
              0,
              0.01,
              0.1,
              CONFIG.viewRatio,
            ],
          }
        )

      : null;



  /* =========================================================
     CONTEXT
     ========================================================= */

  function readContext() {

    const node =
      document.getElementById(
        'SctAnalyticsContext'
      );


    if (!node) {
      return {};
    }


    try {

      return JSON.parse(
        node.textContent || '{}'
      );

    } catch (error) {

      console.warn(
        '[SCT Analytics] Failed to read context.',
        error
      );


      return {};

    }

  }



  function isPreviewMode() {

    return Boolean(

      context.design_mode ||

      context.visual_preview_mode ||

      window.Shopify?.designMode ||

      query.has(
        'preview_theme_id'
      )

    );

  }



  /* =========================================================
     HELPERS
     ========================================================= */

  function cleanPayload(
    payload
  ) {

    return Object.fromEntries(

      Object
        .entries(
          payload
        )
        .filter(
          ([, value]) =>
            value !== undefined &&
            value !== null &&
            value !== ''
        )

    );

  }



  function toNumber(
    value
  ) {

    if (
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return undefined;
    }


    const number =
      Number(
        value
      );


    return Number.isFinite(
      number
    )
      ? number
      : undefined;

  }



  /* =========================================================
     MODULE POSITION
     ========================================================= */

  function getModulePosition(
    moduleElement
  ) {

    const manualPosition =
      toNumber(
        moduleElement.dataset
          .sctModulePosition
      );


    if (manualPosition) {
      return manualPosition;
    }


    const modules =
      Array.from(
        document.querySelectorAll(
          '[data-sct-module]'
        )
      );


    const index =
      modules.indexOf(
        moduleElement
      );


    return index === -1
      ? undefined
      : index + 1;

  }



  /* =========================================================
     SHOPIFY SECTION POSITION
     ========================================================= */

  function getSectionPosition(
    moduleElement
  ) {

    const sectionElement =
      moduleElement.closest(
        '.shopify-section'
      );


    if (!sectionElement) {
      return undefined;
    }


    const sections =
      Array.from(
        document.querySelectorAll(
          '.shopify-section'
        )
      );


    const index =
      sections.indexOf(
        sectionElement
      );


    return index === -1
      ? undefined
      : index + 1;

  }



  /* =========================================================
     PLACEMENT
     ========================================================= */

  function getPlacement(
    moduleElement
  ) {

    const manualPlacement =
      moduleElement.dataset
        .sctPlacement;


    if (
      manualPlacement &&
      manualPlacement !==
        'auto'
    ) {
      return manualPlacement;
    }


    const rect =
      moduleElement
        .getBoundingClientRect();


    const scrollTop =
      window.scrollY ||
      document.documentElement
        .scrollTop ||
      0;


    const moduleCenter =
      scrollTop +
      rect.top +
      rect.height / 2;


    const documentHeight =
      Math.max(

        document.body.scrollHeight,

        document.documentElement
          .scrollHeight,

        document.body.offsetHeight,

        document.documentElement
          .offsetHeight,

        document.body.clientHeight,

        document.documentElement
          .clientHeight

      );


    if (
      !documentHeight ||
      documentHeight <= 0
    ) {
      return undefined;
    }


    const ratio =
      moduleCenter /
      documentHeight;


    if (
      ratio <= 0.33
    ) {
      return 'page_top';
    }


    if (
      ratio <= 0.66
    ) {
      return 'page_middle';
    }


    return 'page_bottom';

  }



  /* =========================================================
     COLLECTION HANDLE
     ========================================================= */

  function getCollectionHandle() {

    const match =
      window.location.pathname.match(
        /^\/collections\/([^/]+)/
      );


    return match?.[1] ||
      undefined;

  }



  /* =========================================================
     COMMON PAYLOAD
     ========================================================= */

  function basePayload(
    moduleElement
  ) {

    return cleanPayload({

      schema_version:
        CONFIG.schemaVersion,

      experiment_id:
        window
          .SctRecommendationExperiment
          ?.id,

      experiment_group:
        window
          .SctRecommendationExperiment
          ?.group,


      module_id:
        moduleElement.dataset
          .sctModule,


      module_instance:
        moduleElement.dataset
          .sctModuleInstance,


      module_name:
        moduleElement.dataset
          .sctModuleName,


      module_position:
        getModulePosition(
          moduleElement
        ),


      section_position:
        getSectionPosition(
          moduleElement
        ),


      placement:
        getPlacement(
          moduleElement
        ),


      page_type:
        context.page_type ||

        document.querySelector(
          'main[data-template]'
        )?.dataset.template,


      page_path:
        window.location.pathname,


      market:
        context.market,


      country:
        context.country,


      language:
        context.language ||

        document.documentElement.lang,


      /*
       * Predictive Searchなどで使用
       */
      search_term:
        moduleElement.dataset
          .sctSearchTerm,

    });

  }



  /* =========================================================
     GA4
     ========================================================= */

  function publishToGA4(
    ga4EventName,
    payload
  ) {

    /*
     * Theme Previewでは
     * 本番GA4へ送らない。
     *
     * 強制送信したい場合のみ
     * ?sct_publish=1
     */
    if (
      previewMode &&
      !forcePublish
    ) {

      if (debug) {

        console.log(
          `[SCT Analytics][GA4 skipped] ${ga4EventName}`,
          payload
        );

      }


      return;

    }


    if (
      typeof window.gtag !==
      'function'
    ) {

      if (debug) {

        console.warn(
          '[SCT Analytics] window.gtag is not available.'
        );

      }


      return;

    }


    const ga4Payload =
      cleanPayload({

        ...payload,


        /*
         * SCULTUREのGA4だけへ送信
         */
        send_to:
          CONFIG.ga4MeasurementId,


        /*
         * ?sct_debug=1 のときは
         * GA4 DebugViewにも送る
         */
        debug_mode:
          debug
            ? true
            : undefined,

      });


    try {

      window.gtag(
        'event',
        ga4EventName,
        ga4Payload
      );


      if (debug) {

        console.log(
          `[SCT Analytics][GA4] ${ga4EventName}`,
          ga4Payload
        );

      }

    } catch (error) {

      console.warn(
        `[SCT Analytics] Failed to send ${ga4EventName} to GA4.`,
        error
      );

    }

  }



  /* =========================================================
     SHOPIFY CUSTOMER EVENT
     ========================================================= */

  function publishToShopify(
    shopifyEventName,
    payload
  ) {

    /*
     * Theme Previewでは
     * Shopify側にも送らない。
     */
    if (
      previewMode &&
      !forcePublish
    ) {

      if (debug) {

        console.log(
          `[SCT Analytics][Shopify skipped] ${shopifyEventName}`,
          payload
        );

      }


      return;

    }


    if (
      !window.Shopify
        ?.analytics
        ?.publish
    ) {

      if (debug) {

        console.warn(
          '[SCT Analytics] Shopify.analytics.publish is not available.'
        );

      }


      return;

    }


    try {

      window.Shopify
        .analytics
        .publish(
          shopifyEventName,
          payload
        );


      if (debug) {

        console.log(
          `[SCT Analytics][Shopify] ${shopifyEventName}`,
          payload
        );

      }

    } catch (error) {

      console.warn(
        `[SCT Analytics] Failed to publish ${shopifyEventName}.`,
        error
      );

    }

  }



  /* =========================================================
     PUBLISH
     Shopify + GA4
     ========================================================= */

  function publish(
    eventType,
    payload
  ) {

    const data =
      cleanPayload(
        payload
      );


    let shopifyEventName;

    let ga4EventName;


    if (
      eventType ===
      'view'
    ) {

      shopifyEventName =
        CONFIG.shopifyEventView;


      ga4EventName =
        CONFIG.ga4EventView;

    } else {

      shopifyEventName =
        CONFIG.shopifyEventClick;


      ga4EventName =
        CONFIG.ga4EventClick;

    }



    if (debug) {

      console.log(
        `[SCT Analytics] ${shopifyEventName}`,
        data,
        {
          previewMode,
          forcePublish,
        }
      );

    }



    publishToShopify(
      shopifyEventName,
      data
    );


    publishToGA4(
      ga4EventName,
      data
    );

  }



  /* =========================================================
     RECOMMENDATION EXPERIMENT
     ========================================================= */

  function experimentPayload(
    payload = {}
  ) {
    const experiment =
      window
        .SctRecommendationExperiment;

    return cleanPayload({
      experiment_id:
        experiment?.id,

      experiment_group:
        experiment?.group,

      page_type:
        context.page_type,

      page_path:
        window.location.pathname,

      market:
        context.market,

      country:
        context.country,

      language:
        context.language ||
        document.documentElement.lang,

      ...payload,
    });
  }

  function publishExperimentAssignment() {
    const experiment =
      window
        .SctRecommendationExperiment;

    if (
      !experiment?.id ||
      !experiment?.group
    ) {
      return;
    }

    const sessionKey =
      'sct_rec_exp_assignment_sent_' +
      experiment.id;

    try {
      if (
        sessionStorage.getItem(
          sessionKey
        ) === experiment.group
      ) {
        return;
      }
    } catch (_error) {
      // no-op
    }

    const payload =
      experimentPayload({
        action_type:
          'experiment_assignment',

        assignment_source:
          experiment.source,
      });

    publishToShopify(
      'sculture:recommendation_experiment_assignment',
      payload
    );

    publishToGA4(
      'sct_rec_experiment_assignment',
      payload
    );

    try {
      sessionStorage.setItem(
        sessionKey,
        experiment.group
      );
    } catch (_error) {
      // no-op
    }
  }

  function publishExperimentOutcome(
    actionType,
    payload = {}
  ) {
    const data =
      experimentPayload({
        action_type:
          actionType,

        ...payload,
      });

    publishToShopify(
      'sculture:recommendation_experiment_outcome',
      data
    );

    publishToGA4(
      'sct_rec_experiment_outcome',
      data
    );
  }

  function flushExperimentOutcomes() {
    const key =
      'sct_rec_experiment_outcomes_v1';

    let queue = [];

    try {
      const raw =
        sessionStorage.getItem(
          key
        );

      const parsed =
        raw
          ? JSON.parse(raw)
          : [];

      queue =
        Array.isArray(parsed)
          ? parsed
          : [];

      sessionStorage.removeItem(
        key
      );
    } catch (_error) {
      queue = [];
    }

    queue.forEach(
      item => {
        if (
          !item ||
          typeof item !==
            'object'
        ) {
          return;
        }

        publishExperimentOutcome(
          item.action_type ||
            'unknown',
          item.payload || {}
        );
      }
    );
  }


  /* =========================================================
     TRACK ACTION
     ========================================================= */

  function trackAction(
    moduleElement,
    payload = {}
  ) {

    if (
      !(
        moduleElement instanceof
        Element
      ) ||

      !moduleElement.dataset
        .sctModule
    ) {
      return;
    }


    publish(
      'click',
      {

        ...basePayload(
          moduleElement
        ),

        ...payload,

      }
    );

  }



  /*
   * 他のSCULTURE JSからも
   * 手動イベント送信可能
   */
  window.SctAnalytics =
    Object.freeze({

      track(
        moduleElement,
        payload = {}
      ) {

        trackAction(
          moduleElement,
          payload
        );

      },

      trackExperiment(
        actionType,
        payload = {}
      ) {
        publishExperimentOutcome(
          actionType,
          payload
        );
      },

    });



  /* =========================================================
     VIEW DETECTION
     ========================================================= */

  function visibleEnough(
    element
  ) {

    const rect =
      element
        .getBoundingClientRect();


    if (
      rect.width <= 0 ||
      rect.height <= 0
    ) {
      return false;
    }


    const viewportWidth =
      window.innerWidth ||

      document.documentElement
        .clientWidth;


    const viewportHeight =
      window.innerHeight ||

      document.documentElement
        .clientHeight;


    const visibleWidth =
      Math.max(

        0,

        Math.min(
          rect.right,
          viewportWidth
        ) -

        Math.max(
          rect.left,
          0
        )

      );


    const visibleHeight =
      Math.max(

        0,

        Math.min(
          rect.bottom,
          viewportHeight
        ) -

        Math.max(
          rect.top,
          0
        )

      );


    const visibleArea =
      visibleWidth *
      visibleHeight;


    const totalArea =
      rect.width *
      rect.height;


    const visibleRatio =
      totalArea > 0
        ? visibleArea /
          totalArea
        : 0;


    return (

      visibleRatio >=
        CONFIG.viewRatio ||

      visibleHeight >=
        CONFIG.minVisibleHeightPx

    );

  }



  function clearViewTimer(
    element
  ) {

    const timer =
      viewTimers.get(
        element
      );


    if (!timer) {
      return;
    }


    window.clearTimeout(
      timer
    );


    viewTimers.delete(
      element
    );

  }



  function scheduleView(
    element
  ) {

    if (
      viewedModules.has(
        element
      ) ||

      viewTimers.has(
        element
      )
    ) {
      return;
    }


    const timer =
      window.setTimeout(
        () => {

          viewTimers.delete(
            element
          );


          if (

            viewedModules.has(
              element
            ) ||

            !document
              .documentElement
              .contains(
                element
              ) ||

            !visibleEnough(
              element
            )

          ) {
            return;
          }


          viewedModules.add(
            element
          );


          publish(
            'view',
            {

              ...basePayload(
                element
              ),


              view_type:
                'viewport',

            }
          );

        },

        CONFIG.viewDelayMs

      );


    viewTimers.set(
      element,
      timer
    );

  }



  function handleIntersections(
    entries
  ) {

    entries.forEach(
      entry => {

        const element =
          entry.target;


        if (
          viewedModules.has(
            element
          )
        ) {

          observer?.unobserve(
            element
          );


          clearViewTimer(
            element
          );


          return;

        }


        const qualifies =

          entry.isIntersecting &&

          (

            entry
              .intersectionRatio >=
              CONFIG.viewRatio ||

            entry
              .intersectionRect
              .height >=
              CONFIG
                .minVisibleHeightPx

          );


        if (qualifies) {

          scheduleView(
            element
          );

        } else {

          clearViewTimer(
            element
          );

        }

      }
    );

  }



  /* =========================================================
     MODULE OBSERVER
     ========================================================= */

  function observeModule(
    element
  ) {

    if (

      !(
        element instanceof
        Element
      ) ||

      observedModules.has(
        element
      ) ||

      !element.dataset
        .sctModule

    ) {
      return;
    }


    observedModules.add(
      element
    );


    if (observer) {

      observer.observe(
        element
      );


      return;

    }


    if (
      visibleEnough(
        element
      )
    ) {

      scheduleView(
        element
      );

    }

  }



  function scanModules(
    root = document
  ) {

    if (

      root instanceof
        Element &&

      root.matches(
        '[data-sct-module]'
      )

    ) {

      observeModule(
        root
      );

    }


    root
      .querySelectorAll?.(
        '[data-sct-module]'
      )
      .forEach(
        observeModule
      );

  }



  /* =========================================================
     DESTINATION
     ========================================================= */

  function destinationData(
    actionElement,
    linkElement = null
  ) {

    const link =

      linkElement ||

      (

        actionElement.matches(
          'a[href]'
        )

          ? actionElement

          : actionElement.closest(
              'a[href]'
            )

      );


    if (!link) {

      return cleanPayload({

        destination_type:
          actionElement.dataset
            .sctDestinationType,

      });

    }


    try {

      const url =
        new URL(
          link.href,
          window.location.origin
        );


      const internal =
        url.origin ===
        window.location.origin;


      return cleanPayload({

        destination_type:
          actionElement.dataset
            .sctDestinationType ||

          (
            internal
              ? 'internal'
              : 'external'
          ),


        destination_path:
          internal
            ? url.pathname
            : undefined,


        destination_host:
          internal
            ? undefined
            : url.hostname,

      });

    } catch (_error) {

      return cleanPayload({

        destination_type:
          actionElement.dataset
            .sctDestinationType,

      });

    }

  }



  /* =========================================================
     NORMAL CLICK
     ========================================================= */

  function handleClick(
    event
  ) {

    const target =

      event.target instanceof
        Element

        ? event.target

        : event.target
            ?.parentElement;


    if (!target) {
      return;
    }


    const moduleElement =
      target.closest(
        '[data-sct-module]'
      );


    if (!moduleElement) {
      return;
    }


    const explicitActionElement =
      target.closest(
        '[data-sct-action]'
      );


    const linkElement =
      target.closest(
        'a[href]'
      );


    let actionElement =
      explicitActionElement;



    /*
     * Dawn card-product本体を
     * 編集しなくても商品クリックを取る
     */
    if (
      !actionElement &&
      linkElement
    ) {

      const itemElement =
        target.closest(
          '[data-sct-item-type]'
        );


      if (

        itemElement &&

        moduleElement.contains(
          itemElement
        )

      ) {

        actionElement =
          itemElement;

      }

    }


    if (!actionElement) {
      return;
    }


    const itemType =
      actionElement.dataset
        .sctItemType;


    const actionType =

      actionElement.dataset
        .sctAction ||

      (

        itemType

          ? `${itemType}_click`

          : 'link_click'

      );


    trackAction(
      moduleElement,
      {

        action_type:
          actionType,


        item_type:
          itemType,


        item_id:
          actionElement.dataset
            .sctItemId,


        item_handle:
          actionElement.dataset
            .sctItemHandle,


        item_name:
          actionElement.dataset
            .sctItemName,


        item_position:
          toNumber(
            actionElement.dataset
              .sctItemPosition
          ),


        outfit_position:
          toNumber(
            actionElement.dataset
              .sctOutfitPosition
          ),


        product_position:
          toNumber(
            actionElement.dataset
              .sctProductPosition
          ),


        tab_name:
          actionElement.dataset
            .sctTabName,


        brand:
          actionElement.dataset
            .sctBrand,


        collection_handle:
          actionElement.dataset
            .sctCollectionHandle,


        ...destinationData(
          actionElement,
          linkElement
        ),

      }
    );

  }



  /* =========================================================
     FILTER HELPERS
     ========================================================= */

  function labelText(
    input
  ) {

    return (

      input
        .closest(
          'label'
        )
        ?.querySelector(
          '.sc-filter-radio__label, .sc-filter-checkbox__label'
        )
        ?.textContent
        ?.trim()

      ||

      input.value

      ||

      undefined

    );

  }



  function mapAvailability(
    value
  ) {

    if (
      value === '1'
    ) {
      return 'in_stock';
    }


    if (
      value === '0'
    ) {
      return 'out_of_stock';
    }


    return 'all';

  }



  /* =========================================================
     CATEGORY FILTER ANALYTICS
     ========================================================= */

  function decorateCategoryFilter(
    root
  ) {

    if (
      decoratedFilters.has(
        root
      )
    ) {
      return;
    }


    decoratedFilters.add(
      root
    );


    root.dataset.sctModule =
      'category_filter';


    root.dataset.sctModuleInstance =
      root.id ||
      'category_filter';


    root.dataset.sctModuleName =
      'Category Filter';



    const trigger =
      root.querySelector(
        '.sc-filter-trigger'
      );


    const applyButton =
      root.querySelector(
        '.sc-filter-submit'
      );


    const resetButton =
      root.querySelector(
        '.sc-filter-reset'
      );


    const brandInputs =
      Array.from(
        root.querySelectorAll(
          'input[name$="-brand"]'
        )
      );


    const availabilityInputs =
      Array.from(
        root.querySelectorAll(
          'input[name$="-availability"]'
        )
      );


    const sortInputs =
      Array.from(
        root.querySelectorAll(
          'input[name$="-sort"]'
        )
      );


    const priceMin =
      root.querySelector(
        '.sc-price-min'
      );


    const priceMax =
      root.querySelector(
        '.sc-price-max'
      );



    /*
     * FILTER OPEN
     */
    trigger?.addEventListener(
      'click',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_open',

            item_type:
              'filter',

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * BRAND
     */
    brandInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              !input.checked
            ) {
              return;
            }


            trackAction(
              root,
              {

                action_type:
                  'filter_select',

                item_type:
                  'filter_brand',

                item_name:
                  input.value ||
                  'All Brands',

                item_handle:
                  input.dataset
                    .tagHandle ||
                  undefined,

                brand:
                  input.value ||
                  undefined,

                filter_type:
                  'brand',

                filter_value:
                  input.value ||
                  'all',

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * AVAILABILITY
     */
    availabilityInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              !input.checked
            ) {
              return;
            }


            trackAction(
              root,
              {

                action_type:
                  'filter_select',

                item_type:
                  'filter_availability',

                item_name:
                  labelText(
                    input
                  ),

                filter_type:
                  'availability',

                filter_value:
                  mapAvailability(
                    input.value
                  ),

                availability:
                  mapAvailability(
                    input.value
                  ),

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * SORT
     */
    sortInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              !input.checked
            ) {
              return;
            }


            trackAction(
              root,
              {

                action_type:
                  'sort_select',

                item_type:
                  'sort',

                item_name:
                  input.dataset
                    .sortLabel ||
                  labelText(
                    input
                  ),

                filter_type:
                  'sort',

                filter_value:
                  input.value,

                sort_by:
                  input.value,

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * PRICE MIN
     */
    priceMin?.addEventListener(
      'change',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_select',

            item_type:
              'filter_price_min',

            item_name:
              priceMin.value ||
              'none',

            filter_type:
              'price_min',

            filter_value:
              priceMin.value ||
              'none',

            price_min:
              priceMin.value ||
              undefined,

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * PRICE MAX
     */
    priceMax?.addEventListener(
      'change',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_select',

            item_type:
              'filter_price_max',

            item_name:
              priceMax.value ||
              'none',

            filter_type:
              'price_max',

            filter_value:
              priceMax.value ||
              'none',

            price_max:
              priceMax.value ||
              undefined,

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * APPLY
     */
    applyButton?.addEventListener(
      'click',
      () => {

        const selectedBrand =
          brandInputs.find(
            input =>
              input.checked
          );


        const selectedAvailability =
          availabilityInputs.find(
            input =>
              input.checked
          );


        const selectedSort =
          sortInputs.find(
            input =>
              input.checked
          );


        trackAction(
          root,
          {

            action_type:
              'filter_apply',

            item_type:
              'filter_state',

            filter_brand:
              selectedBrand?.value ||
              'all',

            price_min:
              priceMin?.value ||
              undefined,

            price_max:
              priceMax?.value ||
              undefined,

            availability:
              mapAvailability(
                selectedAvailability
                  ?.value ||
                ''
              ),

            sort_by:
              selectedSort?.value ||
              undefined,

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * CLEAR
     */
    resetButton?.addEventListener(
      'click',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_clear',

            item_type:
              'filter',

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );

  }



  /* =========================================================
     NORMAL COLLECTION FILTER ANALYTICS
     ========================================================= */

  function decorateCollectionFilter(
    root
  ) {

    if (
      decoratedFilters.has(
        root
      )
    ) {
      return;
    }


    decoratedFilters.add(
      root
    );


    root.dataset.sctModule =
      'collection_filter';


    root.dataset.sctModuleInstance =
      root.id ||
      'collection_filter';


    root.dataset.sctModuleName =
      'Collection Filter';



    const trigger =
      root.querySelector(
        '.sc-filter-trigger'
      );


    const applyButton =
      root.querySelector(
        '.sc-filter-submit'
      );


    const resetButton =
      root.querySelector(
        '.sc-filter-reset'
      );


    const productTypeInputs =
      Array.from(
        root.querySelectorAll(
          'input[name="sc_product_type"]'
        )
      );


    const availabilityInputs =
      Array.from(
        root.querySelectorAll(
          'input[name="filter.v.availability"]'
        )
      );


    const sortInputs =
      Array.from(
        root.querySelectorAll(
          'input[name="sort_by"]'
        )
      );



    /*
     * FILTER OPEN
     */
    trigger?.addEventListener(
      'click',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_open',

            item_type:
              'filter',

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * PRODUCT TYPE
     */
    productTypeInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              !input.checked
            ) {
              return;
            }


            trackAction(
              root,
              {

                action_type:
                  'filter_select',

                item_type:
                  'filter_product_type',

                item_name:
                  labelText(
                    input
                  ) ||
                  'All',

                item_handle:
                  input.value ||
                  undefined,

                filter_type:
                  'product_type',

                filter_value:
                  input.value ||
                  'all',

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * AVAILABILITY
     */
    availabilityInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            trackAction(
              root,
              {

                action_type:
                  'filter_select',

                item_type:
                  'filter_availability',

                item_name:
                  labelText(
                    input
                  ),

                filter_type:
                  'availability',

                filter_value:
                  mapAvailability(
                    input.value
                  ),

                availability:
                  input.checked
                    ? mapAvailability(
                        input.value
                      )
                    : 'removed',

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * SORT
     */
    sortInputs.forEach(
      input => {

        input.addEventListener(
          'change',
          () => {

            if (
              !input.checked
            ) {
              return;
            }


            trackAction(
              root,
              {

                action_type:
                  'sort_select',

                item_type:
                  'sort',

                item_name:
                  labelText(
                    input
                  ),

                filter_type:
                  'sort',

                filter_value:
                  input.value,

                sort_by:
                  input.value,

                collection_handle:
                  getCollectionHandle(),

              }
            );

          },
          true
        );

      }
    );



    /*
     * APPLY
     */
    applyButton?.addEventListener(
      'click',
      () => {

        const selectedProductType =
          productTypeInputs.find(
            input =>
              input.checked
          );


        const selectedAvailability =
          availabilityInputs

            .filter(
              input =>
                input.checked
            )

            .map(
              input =>
                mapAvailability(
                  input.value
                )
            );


        const selectedSort =
          sortInputs.find(
            input =>
              input.checked
          );


        trackAction(
          root,
          {

            action_type:
              'filter_apply',

            item_type:
              'filter_state',

            filter_product_type:
              selectedProductType
                ?.value ||
              'all',

            availability:
              selectedAvailability
                .length > 0

                ? selectedAvailability
                    .join(',')

                : 'all',

            sort_by:
              selectedSort
                ?.value ||
              undefined,

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );



    /*
     * CLEAR
     */
    resetButton?.addEventListener(
      'click',
      () => {

        trackAction(
          root,
          {

            action_type:
              'filter_clear',

            item_type:
              'filter',

            collection_handle:
              getCollectionHandle(),

          }
        );

      },
      true
    );

  }



  /* =========================================================
     FILTER AUTO-DETECT
     ========================================================= */

  function decorateFilters(
    root = document
  ) {

    const categoryRoots =
      [];


    const collectionRoots =
      [];


    if (
      root instanceof
      Element
    ) {

      if (
        root.matches(
          '.sc-tag-brand-filter'
        )
      ) {

        categoryRoots.push(
          root
        );

      }


      if (
        root.matches(
          '.sc-collection-filter'
        )
      ) {

        collectionRoots.push(
          root
        );

      }

    }



    root
      .querySelectorAll?.(
        '.sc-tag-brand-filter'
      )
      .forEach(
        element => {

          categoryRoots.push(
            element
          );

        }
      );



    root
      .querySelectorAll?.(
        '.sc-collection-filter'
      )
      .forEach(
        element => {

          collectionRoots.push(
            element
          );

        }
      );



    categoryRoots.forEach(
      decorateCategoryFilter
    );


    collectionRoots.forEach(
      decorateCollectionFilter
    );

  }



  /* =========================================================
     DYNAMIC MODULES
     ========================================================= */

  function watchDynamicModules() {

    if (
      !(
        'MutationObserver'
        in window
      ) ||

      !document.body
    ) {
      return;
    }


    const mutationObserver =
      new MutationObserver(
        mutations => {

          mutations.forEach(
            mutation => {

              mutation
                .addedNodes
                .forEach(
                  node => {

                    if (
                      !(
                        node instanceof
                        Element
                      )
                    ) {
                      return;
                    }


                    decorateFilters(
                      node
                    );


                    scanModules(
                      node
                    );

                  }
                );

            }
          );

        }
      );


    mutationObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true,
      }
    );

  }



  /* =========================================================
     INIT
     ========================================================= */

  function init() {

    /*
     * Filterへmodule情報を付与
     */
    decorateFilters(
      document
    );


    /*
     * 全分析module監視
     */
    scanModules(
      document
    );


    watchDynamicModules();


    window.setTimeout(
      publishExperimentAssignment,
      1200
    );

    flushExperimentOutcomes();



    /*
     * 共通Click
     */
    document.addEventListener(
      'click',
      handleClick,
      true
    );



    /*
     * Shopify Theme Editor
     */
    document.addEventListener(
      'shopify:section:load',
      event => {

        decorateFilters(
          event.target
        );


        scanModules(
          event.target
        );

      }
    );



    if (debug) {

      console.log(
        '[SCT Analytics] initialized',
        {
          context,
          previewMode,
          forcePublish,

          ga4MeasurementId:
            CONFIG.ga4MeasurementId,

          ga4Available:
            typeof window.gtag ===
              'function',
        }
      );

    }

  }



  if (
    document.readyState ===
      'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );

  } else {

    init();

  }

})();